import { EARTH } from '@trajectory/astro';
import type { ManeuverDto, ManeuverKind, OrbitalElementsDto } from '@trajectory/contracts';
import { NumberField } from '@trajectory/ui';
import { Plus, Trash2 } from 'lucide-react';
import { PanelLearningBanner } from '@/components/PanelLearningBanner';
import { TermHelp } from '@/components/TermHelp';
import { useCatalogOrbitPresets } from '@/hooks/useCatalog';
import { summarizeOrbit } from '@/lib/orbit';
import { addManeuver, removeManeuver, useMissionStore } from '@/stores/missionStore';
import { useSelectionStore } from '@/stores/selectionStore';

const DEFAULT_ORBIT: OrbitalElementsDto = {
  semiMajorAxisMeters: EARTH.equatorialRadius + 400_000,
  eccentricity: 0,
  inclinationDeg: 51.6,
  raanDeg: 0,
  argumentOfPeriapsisDeg: 0,
  trueAnomalyDeg: 0,
};

// A module-level constant so the fallback is referentially stable across
// renders — returning a fresh `[]` literal from inside a zustand selector
// makes every read look like a change to `useSyncExternalStore` and trips
// React's "getSnapshot should be cached" loop guard.
const EMPTY_MANEUVERS: ManeuverDto[] = [];

const MANEUVER_KINDS: ManeuverKind[] = [
  'prograde',
  'retrograde',
  'normal',
  'anti-normal',
  'radial-in',
  'radial-out',
  'hohmann-transfer',
  'bi-elliptic-transfer',
  'plane-change',
  'combined-plane-change',
  'phasing',
  'circularise',
  'deorbit',
  'escape',
  'lambert',
];

const MANEUVER_DESCRIPTIONS: Record<ManeuverKind, string> = {
  prograde: 'Accelerates along the direction of travel, usually raising the opposite apsis.',
  retrograde: 'Accelerates opposite the direction of travel, usually lowering the opposite apsis.',
  normal: 'Burns north of the orbital plane to change inclination and plane orientation.',
  'anti-normal': 'Burns south of the orbital plane to change inclination and plane orientation.',
  'radial-in': 'Burns toward the central body, rotating the orbit within its plane.',
  'radial-out': 'Burns away from the central body, rotating the orbit within its plane.',
  'hohmann-transfer': 'Uses a two-impulse, half-ellipse transfer between near-circular coplanar orbits.',
  'bi-elliptic-transfer': 'Uses three impulses and a high intermediate apoapsis for some large radius changes.',
  'plane-change': 'Changes orbital-plane direction; it is cheapest where orbital speed is low.',
  'combined-plane-change': 'Combines plane and speed changes into one vector maneuver.',
  phasing: 'Temporarily changes orbital period to adjust relative position for rendezvous.',
  circularise: 'Raises or lowers one apsis until periapsis and apoapsis match.',
  deorbit: 'Lowers periapsis into the atmosphere or surface for disposal or entry.',
  escape: 'Raises orbital energy to an unbound parabolic or hyperbolic trajectory.',
  lambert: 'Targets a position at a specified future time using a Lambert transfer solution.',
};

// Altitude fields round-trip through metres->apsides->elements->apsides,
// which leaves sub-millimetre float noise (e.g. "400.0000000000028"); round
// the *displayed* km value away rather than showing it to the user.
const roundKm = (meters: number): number => Math.round(meters / 10) / 100;

function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds)) return '∞';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.round(seconds % 60);
  return `${hours}h ${minutes}m ${secs}s`;
}

function apsidesToElements(current: OrbitalElementsDto, periapsisAltitudeMeters: number, apoapsisAltitudeMeters: number): Partial<OrbitalElementsDto> {
  const rp = EARTH.equatorialRadius + periapsisAltitudeMeters;
  const ra = EARTH.equatorialRadius + Math.max(apoapsisAltitudeMeters, periapsisAltitudeMeters);
  return {
    semiMajorAxisMeters: (rp + ra) / 2,
    eccentricity: (ra - rp) / (ra + rp),
  };
}

function ManeuverRow({ maneuver }: { maneuver: ManeuverDto }) {
  const selection = useSelectionStore((state) => state.selection);
  const select = useSelectionStore((state) => state.select);
  const isSelected = selection?.kind === 'maneuver' && selection.maneuverId === maneuver.id;

  return (
    <div
      onClick={() => select({ kind: 'maneuver', maneuverId: maneuver.id })}
      className={`flex items-center gap-2 rounded-control border px-2 py-1.5 text-xs ${
        isSelected ? 'border-accent' : 'border-border'
      }`}
    >
      <span className="flex-1 truncate text-text">{maneuver.name || maneuver.kind}</span>
      <span className="text-faint">t+{maneuver.startTimeSeconds.toFixed(0)}s</span>
      <span className="text-muted">{maneuver.deltaVMetersPerSecond.toFixed(0)} m/s</span>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          removeManeuver(maneuver.id);
        }}
        aria-label={`Remove maneuver ${maneuver.name || maneuver.kind}`}
        title="Remove this maneuver from the mission"
        className="rounded-control p-1 text-faint hover:text-danger"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export function OrbitEditorPanel() {
  const missionId = useMissionStore((state) => state.id);
  const targetOrbit = useMissionStore((state) => state.document?.targetOrbit);
  const maneuvers = useMissionStore((state) => state.document?.maneuvers ?? EMPTY_MANEUVERS);
  const patch = useMissionStore((state) => state.patch);
  const presets = useCatalogOrbitPresets();

  if (!missionId) {
    return (
      <div className="flex h-full items-center justify-center p-4 text-center text-xs text-faint">
        Select a mission in the Mission Explorer to edit its target orbit.
      </div>
    );
  }

  if (!targetOrbit) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-4 text-center text-xs text-faint">
        <p>This mission has no target orbit yet.</p>
        <button
          type="button"
          onClick={() => patch({ targetOrbit: DEFAULT_ORBIT })}
          className="rounded-control border border-border bg-surface-raised px-3 py-1.5 text-text hover:bg-surface-sunken"
        >
          Set a 400 km LEO parking orbit
        </button>
      </div>
    );
  }

  const summary = summarizeOrbit(targetOrbit);
  const setOrbit = (update: Partial<OrbitalElementsDto>) => patch({ targetOrbit: { ...targetOrbit, ...update } });

  return (
    <div className="flex h-full flex-col gap-4 overflow-auto p-3 text-sm">
      <PanelLearningBanner panelId="orbit-editor" />
      {presets.data && presets.data.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {presets.data.map((preset) => (
            <button
              key={preset.id}
              type="button"
              title={preset.description}
              onClick={() =>
                setOrbit({
                  ...apsidesToElements(targetOrbit, preset.altitudeMeters, preset.altitudeMeters),
                  eccentricity: preset.eccentricity,
                  inclinationDeg: preset.inclinationDeg ?? targetOrbit.inclinationDeg,
                })
              }
              className="rounded-control border border-border px-2 py-1 text-xs text-muted hover:border-accent hover:text-text"
            >
              {preset.name}
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <NumberField
          label={<TermHelp termId="periapsis">Periapsis altitude (km)</TermHelp>}
          value={roundKm(summary.periapsisAltitude)}
          hint="Closest orbital altitude. Values inside the atmosphere will decay."
          onChange={(km) => setOrbit(apsidesToElements(targetOrbit, km * 1000, summary.apoapsisAltitude))}
        />
        <NumberField
          label={<TermHelp termId="apoapsis">Apoapsis altitude (km)</TermHelp>}
          value={roundKm(Number.isFinite(summary.apoapsisAltitude) ? summary.apoapsisAltitude : summary.periapsisAltitude)}
          hint="Farthest altitude of a bound orbit."
          onChange={(km) => setOrbit(apsidesToElements(targetOrbit, summary.periapsisAltitude, km * 1000))}
        />
        <NumberField
          label={<TermHelp termId="inclination">Inclination (°)</TermHelp>}
          value={targetOrbit.inclinationDeg}
          min={0}
          max={180}
          hint="Tilt relative to the equator: 0° equatorial, 90° polar."
          onChange={(inclinationDeg) => setOrbit({ inclinationDeg })}
        />
        <NumberField
          label={<TermHelp termId="raan">RAAN (°)</TermHelp>}
          value={targetOrbit.raanDeg}
          min={0}
          max={360}
          hint="Rotates the orbital plane around the body’s spin axis."
          onChange={(raanDeg) => setOrbit({ raanDeg })}
        />
        <NumberField
          label={<TermHelp termId="argument-of-periapsis">Argument of periapsis (°)</TermHelp>}
          value={targetOrbit.argumentOfPeriapsisDeg}
          min={0}
          max={360}
          hint="Locates periapsis within the orbital plane."
          onChange={(argumentOfPeriapsisDeg) => setOrbit({ argumentOfPeriapsisDeg })}
        />
        <NumberField
          label={<TermHelp termId="true-anomaly">True anomaly (°)</TermHelp>}
          value={targetOrbit.trueAnomalyDeg}
          min={0}
          max={360}
          hint="Locates the spacecraft on its orbit at the mission epoch."
          onChange={(trueAnomalyDeg) => setOrbit({ trueAnomalyDeg })}
        />
      </div>

      <div className="grid grid-cols-2 gap-1 rounded-panel border border-border bg-surface-raised p-3 text-xs text-muted">
        <span>Period</span>
        <span className="text-right text-text">{formatDuration(summary.period)}</span>
        <span>Periapsis speed</span>
        <span className="text-right text-text">{summary.periapsisSpeed.toFixed(0)} m/s</span>
        <span>Apoapsis speed</span>
        <span className="text-right text-text">{Number.isFinite(summary.apoapsisSpeed) ? `${summary.apoapsisSpeed.toFixed(0)} m/s` : '—'}</span>
        <span>Type</span>
        <span className="text-right capitalize text-text">{summary.type}</span>
        {summary.willDecay && <span className="col-span-2 text-warning">Periapsis is inside the atmosphere — this orbit will decay.</span>}
        {summary.impacts && <span className="col-span-2 text-danger">This orbit intersects the surface.</span>}
      </div>

      <div>
        <div className="mb-1 flex items-center justify-between">
          <h3 className="text-xs font-medium text-muted">Maneuvers</h3>
          <button
            type="button"
            onClick={() =>
              addManeuver({
                id: crypto.randomUUID(),
                name: 'New maneuver',
                kind: 'prograde',
                startTimeSeconds: 0,
                deltaVMetersPerSecond: 0,
              })
            }
            title="Add a zero-delta-v prograde maneuver, then edit it in Inspector"
            className="flex items-center gap-1 rounded-control p-1 text-faint hover:text-text"
          >
            <Plus className="h-3.5 w-3.5" /> Add
          </button>
        </div>
        <div className="flex flex-col gap-1">
          {maneuvers.length === 0 && <p className="text-xs text-faint">No maneuvers planned.</p>}
          {maneuvers.map((maneuver) => (
            <ManeuverRow key={maneuver.id} maneuver={maneuver} />
          ))}
        </div>
      </div>
    </div>
  );
}

export function maneuverKindOptions() {
  return MANEUVER_KINDS.map((kind) => ({
    value: kind,
    label: kind.replaceAll('-', ' '),
  }));
}

export function maneuverKindDescription(kind: ManeuverKind): string {
  return MANEUVER_DESCRIPTIONS[kind];
}
